import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CalendarDays, ChevronDown, LayoutDashboard, Menu, Plus, Settings, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import UserMenuButton from "@/components/navigation/UserMenuButton";
import AppHeader from "@/components/navigation/AppHeader";
import { canCreateDienstplan, canManagePlan } from "@/lib/roles";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
const MEMBER_SELECTED_PLAN_KEY = "member_selected_plan_id";

const OwnerSidebar = ({ user, onLogout, currentPlanId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/dienstplan`);
        setPlans(response.data || []);
      } catch (error) {
        console.error("Error loading plans for navbar:", error);
      }
    };

    loadPlans();
  }, []);

  const routePlanId = useMemo(() => {
    const match = location.pathname.match(/^\/dienstplan\/([^/]+)/i);
    return match?.[1] || null;
  }, [location.pathname]);

  const selectedPlan = useMemo(() => {
    const selectedId = currentPlanId || routePlanId;
    if (selectedId) {
      const found = plans.find((plan) => String(plan.id) === String(selectedId));
      if (found) return found;
    }
    return plans[0] || null;
  }, [plans, currentPlanId, routePlanId]);
  const dashboardPath = selectedPlan ? `/dienstplan/${selectedPlan.id}` : "/dienstplan";
  const eventsPath = selectedPlan ? `/dienstplan/${selectedPlan.id}/veranstaltungen` : "/dienstplan";
  const teamPath = selectedPlan ? `/dienstplan/${selectedPlan.id}/personal` : "/dienstplan";
  const settingsPath = selectedPlan ? `/dienstplan/${selectedPlan.id}/einstellungen` : "/dienstplan";
  const selectedPlanCanManage = canManagePlan(selectedPlan);
  const getPlanScopedPath = (planId) => {
    const currentPath = location.pathname || "";
    const match = currentPath.match(/^\/dienstplan\/[^/]+(\/.*)?$/i);
    if (match) {
      const suffix = match[1] || "";
      return `/dienstplan/${planId}${suffix}`;
    }
    return `/dienstplan/${planId}`;
  };
  const handlePlanSelect = (plan) => {
    if (!plan?.id) return;

    localStorage.setItem(MEMBER_SELECTED_PLAN_KEY, String(plan.id));

    if (canManagePlan(plan)) {
      navigate(getPlanScopedPath(plan.id));
      return;
    }

    navigate("/member");
  };
  const handleSelectedPlanNavigation = (targetPath) => {
    if (!selectedPlan?.id) {
      navigate("/dienstplan");
      return;
    }

    localStorage.setItem(MEMBER_SELECTED_PLAN_KEY, String(selectedPlan.id));

    if (!selectedPlanCanManage) {
      navigate("/member");
      return;
    }

    navigate(targetPath);
  };

  return (
    <AppHeader
      homePath={dashboardPath}
      center={
        <>
          <Link to={dashboardPath}>
            <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>

          <Link to={eventsPath}>
            <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white">
              <CalendarDays className="w-4 h-4 mr-2" />
              Veranstaltungen
            </Button>
          </Link>

          <Link to={teamPath}>
            <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white">
              <Users className="w-4 h-4 mr-2" />
              Personal
            </Button>
          </Link>

          <Link to={settingsPath}>
            <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white">
              <Settings className="w-4 h-4 mr-2" />
              Einstellungen
            </Button>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white">
                <span className="text-gray-300 mr-1">Dienstplan:</span>
                <span className="max-w-[190px] truncate">{selectedPlan?.name || "Nicht ausgewählt"}</span>
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60">
              {plans.map((plan) => (
                <DropdownMenuItem
                  key={plan.id}
                  onClick={() => handlePlanSelect(plan)}
                  className={`cursor-pointer ${selectedPlan?.id === plan.id ? "font-semibold text-[#17c671]" : ""}`}
                >
                  {plan.name}
                </DropdownMenuItem>
              ))}
              {canCreateDienstplan(user) ? (
                <>
                  {plans.length > 0 ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuItem asChild>
                    <Link to="/dienstplan?create=1" className="cursor-pointer">
                      <Plus className="w-4 h-4 mr-2" />
                      Neuen Dienstplan hinzufügen
                    </Link>
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
      actions={
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="md:hidden h-9 w-9 rounded-full p-0 border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
                aria-label="Menü öffnen"
              >
                <Menu className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Navigation</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleSelectedPlanNavigation(dashboardPath)} className="cursor-pointer">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSelectedPlanNavigation(eventsPath)} className="cursor-pointer">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Veranstaltungen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSelectedPlanNavigation(teamPath)} className="cursor-pointer">
                  <Users className="w-4 h-4 mr-2" />
                  Personal
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSelectedPlanNavigation(settingsPath)} className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Einstellungen
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Dienstpläne</DropdownMenuLabel>
              {plans.map((plan) => (
                <DropdownMenuItem
                  key={plan.id}
                  onClick={() => handlePlanSelect(plan)}
                  className={`cursor-pointer ${selectedPlan?.id === plan.id ? "font-semibold text-[#17c671]" : ""}`}
                >
                  {plan.name}
                </DropdownMenuItem>
              ))}
              {canCreateDienstplan(user) ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dienstplan?create=1" className="cursor-pointer">
                      <Plus className="w-4 h-4 mr-2" />
                      Neuen Dienstplan hinzufügen
                    </Link>
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
          <UserMenuButton user={user} onLogout={onLogout} />
        </>
      }
    />
  );
};

export default OwnerSidebar;
